import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { ILocation } from 'shared/types/location';
import {
  ERegionalDistrictsStatus,
  IDirectionsModalState,
  IGeoState,
  IRegionalDistrict,
  ISelectedRegionalDistrict,
} from 'shared/types/geo';
import {
  IDistrictsDataPayload,
  IGetCitiesByRegionActionPayload,
  IRequestDirectionsDataPayload,
  IRequestRegionalDistrictsDataPayload,
  ISelectCityActionPayload,
  ISelectRegionActionPayload,
} from './types';
import { IJsonQueryDistrict, IJsonQueryRegionalDistrict } from '../../../packages/api-models/common/json_query';
import {
  loadLocationSwitcherData,
  makeAutocompleteSearch,
  selectCityBySuggest,
  selectRegion,
} from 'shared/thunks/geo/locationSwitcher';
import { loadDistrictsTree } from 'shared/thunks/geo/districtsSwitcher';
import { loadDirectionsData } from 'shared/thunks/geo/directions';
import { loadRegionalDistricts } from 'shared/thunks/geo/regionalDistricts';

const initialState: IGeoState = {
  regionalDistrictsModal: {
    status: ERegionalDistrictsStatus.Initial,
    districts: [],
    selectedDistricts: [],
  },
  directionsModal: {
    isLoading: false,
    data: [],
  },
  locationSwitcher: {
    isCitiesLoading: false,
    isAutocompleteLoading: false,
    isLoading: false,
  },
  districtsSwitcher: {
    isLoading: false,
    selectedDistricts: [],
  },
  userInput: '',
};

export const geoSlice = createSlice({
  name: 'geo',
  initialState,
  reducers: {
    clearLocationSwitcherData: state => {
      state.locationSwitcher.selectedRegion = undefined;
      state.locationSwitcher.selectedCity = undefined;
      state.locationSwitcher.cities = undefined;
      state.locationSwitcher.isCitiesLoading = false;
    },
    updateCity: (state, action: PayloadAction<ILocation>) => {
      state.locationSwitcher.selectedCity = action.payload;
    },
    setRegionalDistricts: (state, action: PayloadAction<IRegionalDistrict[]>) => {
      state.regionalDistrictsModal.districts = action.payload;
      state.regionalDistrictsModal.status = ERegionalDistrictsStatus.Success;
    },
    addRegionalDistrict: (state, action: PayloadAction<ISelectedRegionalDistrict>) => {
      state.regionalDistrictsModal.selectedDistricts = [
        ...state.regionalDistrictsModal.selectedDistricts,
        { id: action.payload.id, type: 'regional_district_id' },
      ];
    },
    removeRegionalDistrict: (state, action: PayloadAction<ISelectedRegionalDistrict>) => {
      state.regionalDistrictsModal.selectedDistricts = state.regionalDistrictsModal.selectedDistricts.filter(
        district => district.id !== action.payload.id,
      );
    },
    setRegionalDistrictsError: state => {
      state.regionalDistrictsModal.status = ERegionalDistrictsStatus.Failed;
    },
    setRegionalDistrictsReady: state => {
      state.regionalDistrictsModal.status = ERegionalDistrictsStatus.Ready;
    },
    applyLocation: state => {
      state.districtsSwitcher.districts = undefined;
      state.districtsSwitcher.selectedLocation = undefined;
      state.districtsSwitcher.selectedDistricts = [];
    },
    setSelectedDistricts: (state, action: PayloadAction<IJsonQueryDistrict[]>) => {
      state.districtsSwitcher.selectedDistricts = action.payload;
    },
    setUserInput: (state, action: PayloadAction<string>) => {
      state.userInput = action.payload;
    },
    setDirectionsModalData: (state, action: PayloadAction<Partial<IDirectionsModalState>>) => {
      state.directionsModal = {
        ...state.directionsModal,
        isLoading: false,
        ...action.payload,
      };
    },
    setRegionalDistrictsStatus: (state, action: PayloadAction<ERegionalDistrictsStatus>) => {
      state.regionalDistrictsModal.status = action.payload;
    },
    setSelectedRegionalDistricts: (state, action: PayloadAction<IJsonQueryRegionalDistrict[]>) => {
      state.regionalDistrictsModal.selectedDistricts = action.payload;
    },
    setLocationSwitcherCitiesLoading: (state, action: PayloadAction<boolean>) => {
      state.locationSwitcher.isCitiesLoading = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(loadLocationSwitcherData.pending, state => {
        state.locationSwitcher.isLoading = true;
      })
      .addCase(loadLocationSwitcherData.fulfilled, (state, action: PayloadAction<IGetCitiesByRegionActionPayload>) => {
        state.locationSwitcher = {
          ...state.locationSwitcher,
          ...action.payload,
        };
      })
      .addCase(makeAutocompleteSearch.pending, state => {
        state.locationSwitcher.isAutocompleteLoading = true;
      })
      .addCase(makeAutocompleteSearch.fulfilled, (state, action: PayloadAction<ILocation[]>) => {
        state.locationSwitcher.autocompleteSuggestions = action.payload;
      })
      .addCase(selectRegion.fulfilled, (state, action: PayloadAction<ISelectRegionActionPayload>) => {
        state.locationSwitcher.cities = action.payload.cities;
        state.locationSwitcher.selectedRegion = action.payload.region;
        state.locationSwitcher.selectedCity = undefined;
      })
      .addCase(loadDirectionsData.pending, state => {
        state.directionsModal.isLoading = true;
      })
      .addCase(loadDirectionsData.fulfilled, (state, action: PayloadAction<IRequestDirectionsDataPayload>) => {
        state.directionsModal.data = action.payload.directionsData;
        state.directionsModal.isLoading = false;
        state.directionsModal.regionId = action.payload.regionId;
      })
      .addCase(loadDistrictsTree.pending, state => {
        state.districtsSwitcher.isLoading = true;
      })
      .addCase(loadDistrictsTree.fulfilled, (state, action: PayloadAction<IDistrictsDataPayload>) => {
        state.districtsSwitcher.districts = action.payload.districts;
        state.districtsSwitcher.selectedDistricts = action.payload.selectedDistricts;
        state.districtsSwitcher.isLoading = false;
      })
      .addCase(loadRegionalDistricts.pending, state => {
        state.regionalDistrictsModal.status = ERegionalDistrictsStatus.Loading;
      })
      .addCase(
        loadRegionalDistricts.fulfilled,
        (state, action: PayloadAction<IRequestRegionalDistrictsDataPayload>) => {
          state.regionalDistrictsModal.districts = action.payload.regionalDistricts;
          state.regionalDistrictsModal.status = ERegionalDistrictsStatus.Success;
          state.regionalDistrictsModal.regionId = action.payload.regionId;
        },
      )
      .addCase(loadRegionalDistricts.rejected, state => {
        state.regionalDistrictsModal.status = ERegionalDistrictsStatus.Failed;
      })
      .addCase(selectCityBySuggest.pending, state => {
        state.locationSwitcher.isCitiesLoading = true;
      })
      .addCase(selectCityBySuggest.fulfilled, (state, action: PayloadAction<ISelectCityActionPayload>) => {
        state.locationSwitcher.cities = action.payload.cities;
        state.locationSwitcher.selectedCity = action.payload.city;
        state.locationSwitcher.selectedRegion = action.payload.selectedRegion;
      })
      .addMatcher(isAnyOf(selectCityBySuggest.fulfilled, selectCityBySuggest.rejected), state => {
        state.locationSwitcher.isCitiesLoading = false;
      })
      .addMatcher(isAnyOf(loadLocationSwitcherData.fulfilled, loadLocationSwitcherData.rejected), state => {
        state.locationSwitcher.isLoading = false;
      })
      .addMatcher(isAnyOf(makeAutocompleteSearch.fulfilled, makeAutocompleteSearch.rejected), state => {
        state.locationSwitcher.isAutocompleteLoading = false;
      });
  },
});

export const {
  reducer: geoReducer,
  name: geoReducerName,
  actions: {
    clearLocationSwitcherData,
    updateCity,
    setRegionalDistricts,
    setRegionalDistrictsError,
    setRegionalDistrictsReady,
    addRegionalDistrict,
    removeRegionalDistrict,
    applyLocation,
    setSelectedDistricts,
    setUserInput,
    setDirectionsModalData,
    setRegionalDistrictsStatus,
    setSelectedRegionalDistricts,
    setLocationSwitcherCitiesLoading,
  },
} = geoSlice;
